import { useEffect, useState } from 'react';

export default function LightboxModal({ isOpen, onClose, images, currentIndex, setCurrentIndex, caption, title }) {
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !images || images.length === 0) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > 50) {
      // Swipe left -> next image
      handleNext();
    } else if (distance < -50) {
      // Swipe right -> prev image
      handlePrev();
    }
    setTouchStartX(0);
    setTouchEndX(0);
  };

  const currentImage = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6 select-none animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
    >
      {/* Top Header */}
      <div className="flex items-center justify-between text-white z-10 py-2">
        <div className="flex items-center gap-3">
          <span className="font-heading font-bold text-lg text-[#9ecc49]">{title || 'Galleri'}</span>
          <span className="text-xs bg-white/10 px-2.5 py-1 rounded-full text-gray-300">
            {currentIndex + 1} / {images.length}
          </span>
        </div>

        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#9ecc49]"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
        </button>
      </div>

      {/* Main image container */}
      <div
        className="relative flex-1 flex items-center justify-center my-4 overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          key={currentIndex}
          src={currentImage}
          alt={caption || title || `Billede ${currentIndex + 1}`}
          className="max-h-[75vh] max-w-full object-contain rounded shadow-2xl animate-zoom-in transition-all duration-300"
        />

        {/* Previous Button */}
        {images.length > 1 && (
          <button
            onClick={handlePrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-[#9ecc49] hover:text-[#141f14] text-white flex items-center justify-center transition-all border border-white/20 focus:outline-none"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
          </button>
        )}

        {/* Next Button */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-[#9ecc49] hover:text-[#141f14] text-white flex items-center justify-center transition-all border border-white/20 focus:outline-none"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Bottom Caption & Thumbnails */}
      <div className="z-10 text-center max-w-2xl mx-auto space-y-3">
        {caption && (
          <p className="text-sm text-gray-200 bg-black/40 px-4 py-2 rounded border border-white/10 inline-block">
            {caption}
          </p>
        )}

        {/* Thumbnails row */}
        {images.length > 1 && (
          <div className="flex justify-center gap-2 overflow-x-auto py-2 px-2 max-w-full">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                  idx === currentIndex ? 'border-[#9ecc49] scale-105 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img src={img} alt={`Miniature ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
